import React from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { FiEdit2, FiCopy, FiExternalLink } from 'react-icons/fi';
import { DropDownMenu, MESSAGE_MENU_Z_INDEX } from '../Messages/MessageContextMenu';
import { openExternalUrl } from '../../util/downloadHelpers';

export default function TitleLinkMenu({ link, onClose, onEdit, onCopied }) {
    const { t } = useTranslation();
    const menuRef = React.useRef(null);
    React.useLayoutEffect(() => {
        const menu = menuRef.current;
        const anchor = link.anchor;
        const anchorRect = anchor.getBoundingClientRect();
        const menuRect = menu.getBoundingClientRect();
        menu.style.left = `${Math.max(8, Math.min(anchorRect.left, window.innerWidth - menuRect.width - 8))}px`;
        menu.style.top = `${Math.max(8, Math.min(anchorRect.bottom + 4, window.innerHeight - menuRect.height - 8))}px`;
        menu.querySelector('button')?.focus({ preventScroll: true });
        const dismiss = event => {
            if (event.type === 'scroll' && menu.contains(event.target)) return;
            onClose();
        };
        window.addEventListener('resize', dismiss);
        window.addEventListener('scroll', dismiss, true);
        return () => {
            window.removeEventListener('resize', dismiss);
            window.removeEventListener('scroll', dismiss, true);
        };
    }, [link, onClose]);
    const closeAndRestoreFocus = () => {
        onClose();
        link.anchor?.focus();
    };
    return createPortal(
        <Backdrop
            data-title-link-menu-backdrop
            onMouseDown={event => { event.preventDefault(); event.stopPropagation(); }}
            onClick={event => { event.stopPropagation(); closeAndRestoreFocus(); }}
            onContextMenu={event => { event.preventDefault(); event.stopPropagation(); closeAndRestoreFocus(); }}
            onKeyDown={event => {
                event.stopPropagation();
                if (event.key === 'Escape' || event.key === 'Tab') {
                    event.preventDefault();
                    closeAndRestoreFocus();
                }
            }}
        >
            <Menu
                ref={menuRef}
                role="menu"
                onClick={event => event.stopPropagation()}
                onKeyDown={event => {
                    if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
                    event.preventDefault();
                    const buttons = Array.from(menuRef.current.querySelectorAll('button'));
                    const current = buttons.indexOf(document.activeElement);
                    const index = event.key === 'Home' ? 0 : event.key === 'End' ? buttons.length - 1 : (current + (event.key === 'ArrowDown' ? 1 : -1) + buttons.length) % buttons.length;
                    buttons[index]?.focus();
                }}
            >
                {onEdit ? <MenuButton role="menuitem" type="button" onClick={() => { onClose(); onEdit(); }}><FiEdit2 />{t('common:edit')}</MenuButton> : null}
                <MenuButton role="menuitem" type="button" onClick={async () => {
                    try {
                        await navigator.clipboard.writeText(link.href);
                        closeAndRestoreFocus();
                        onCopied();
                    } catch { /* Keep the menu available if clipboard access fails. */ }
                }}><FiCopy />{t('common:copy')}</MenuButton>
                <MenuButton role="menuitem" type="button" onClick={() => { closeAndRestoreFocus(); openExternalUrl(link.href); }}><FiExternalLink />{t('common:open')}</MenuButton>
            </Menu>
        </Backdrop>, document.body
    );
}

const Backdrop = styled.div`
    position: fixed;
    inset: 0;
    z-index: ${MESSAGE_MENU_Z_INDEX};
    cursor: default;
`;
const Menu = styled(DropDownMenu)`
    position: fixed;
    min-width: 180px;
    max-width: calc(100vw - 16px);
    max-height: calc(100vh - 16px);
    overflow-y: auto;
    padding: 4px;
    box-sizing: border-box;
`;
const MenuButton = styled.button`
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;
    border: 0;
    border-radius: 8px;
    background: transparent;
    color: inherit;
    padding: 9px 10px;
    font: inherit;
    font-size: 13px;
    line-height: 18px;
    text-align: left;
    cursor: pointer;
    &:hover, &:focus-visible { background: rgba(255, 255, 255, 0.08); outline: none; }
`;
